import { Link, useParams } from 'react-router';
import { ExternalLink, FolderGit2, Rocket } from 'lucide-react';
import { ApiError } from '../../api/client';
import { useProject } from '../../api/queries';
import { SilenceMenu } from '../../components/notifications/SilenceMenu';
import { IntegrationsTab } from './IntegrationsTab';
import { ErrorAlert } from '../../components/ErrorAlert';
import { IconChip, PageHeader } from '../../components/PageHeader';
import { PageSkeleton } from '../../components/Skeleton';
import { StatusBadge } from '../../components/StatusBadge';
import { TabBar, useTab, type TabDef } from '../../components/Tabs';
import NotFoundPage from '../NotFoundPage';
import { AuditLog } from '../audit/AuditLog';
import { ConfigTab } from './config/ConfigTab';
import { DeployTab } from './deploy/DeployTab';
import { EnvironmentsTab } from './EnvironmentsTab';
import { ProjectOverviewTab } from './ProjectOverviewTab';
import { ReleasesTab } from './ReleasesTab';

export default function ProjectPage() {
  const { key = '' } = useParams();
  const q = useProject(key);
  const tabs: TabDef[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'deploy', label: 'Deploy' },
    { id: 'environments', label: 'Environments' },
    { id: 'config', label: 'Configuration' },
    { id: 'releases', label: 'Releases' },
    { id: 'integrations', label: 'Integrations' },
    { id: 'activity', label: 'Activity' },
  ];
  const tab = useTab(tabs);

  if (q.isPending) return <PageSkeleton />;
  if (q.isError) {
    if (q.error instanceof ApiError && (q.error.status === 404 || q.error.status === 403)) {
      return q.error.status === 404 ? <NotFoundPage /> : <ErrorAlert error={q.error} />;
    }
    return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  }
  const p = q.data;

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Projects', to: '/projects' }, { label: p.key }]}
        title={p.name}
        icon={<IconChip icon={FolderGit2} tone="primary" size="lg" />}
        badges={
          <>
            <span className="badge badge-soft badge-secondary" title="Your GitLab role on this project">
              {p.role}
            </span>
            <StatusBadge kind="registration" value={p.registrationState} />
          </>
        }
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-mono">{p.key}</span>
            {p.gitlab.webUrl ? (
              <a href={p.gitlab.webUrl} className="link inline-flex items-center gap-1" target="_blank" rel="noreferrer">
                {p.gitlab.path} <ExternalLink className="size-3" aria-hidden="true" />
              </a>
            ) : (
              <span>{p.gitlab.path}</span>
            )}
            {p.description ? <span>{p.description}</span> : null}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <SilenceMenu projectKey={p.key} />
            {tab !== 'deploy' && p.deployable ? (
              <Link to="?tab=deploy" className="btn btn-primary btn-sm">
                <Rocket className="size-4" aria-hidden="true" />
                Deploy
              </Link>
            ) : null}
          </div>
        }
      />
      <TabBar tabs={tabs} active={tab} label="Project sections" />
      <div key={tab} className="mt-6 animate-rise" role="tabpanel">
        {tab === 'overview' ? <ProjectOverviewTab project={p} /> : null}
        {tab === 'deploy' ? <DeployTab project={p} /> : null}
        {tab === 'environments' ? <EnvironmentsTab project={p} /> : null}
        {tab === 'config' ? <ConfigTab project={p} /> : null}
        {tab === 'releases' ? <ReleasesTab project={p} /> : null}
        {tab === 'integrations' ? <IntegrationsTab project={p} /> : null}
        {tab === 'activity' ? <AuditLog filters={{ project: p.key }} /> : null}
      </div>
    </>
  );
}
